import { ProgramStatus, type ProgramWithDetail } from "@humanex/interfaces";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuDivider,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Table,
  TableColumn
} from "@humanex/ui/components";
import { toast, useI18n } from "@humanex/ui/libs";
import { createFileRoute, useNavigate, useRouteContext } from "@tanstack/react-router";
import { MoreHorizontal } from "lucide-react";
import { useState } from "react";

import {
  useCloseProgram,
  useDeleteProgram,
  usePauseProgram,
  useProgramsQuery,
  usePublishProgram,
  useResumeProgram,
  useSubmitForReview
} from "@/hooks/queries/use-program-query";

export const Route = createFileRoute("/_authenticated/org/$orgId/program/")({
  component: RouteComponent
});

const PROGRAM_TYPE_LABELS: Record<string, string> = {
  DU_HOC: "Du học",
  VIEC_LAM: "Việc làm",
  XUAT_KHAU: "Xuất khẩu lao động",
  HOC_NGHE: "Học nghề"
};

const PROGRAM_STATUS_LABELS: Record<string, string> = {
  DRAFT: "Nháp",
  PENDING_REVIEW: "Chờ duyệt",
  REJECTED: "Bị từ chối",
  VERIFIED: "Đã duyệt",
  PUBLISHED: "Đang mở",
  PAUSED: "Tạm dừng",
  CLOSED: "Đã đóng"
};

const PROGRAM_STATUS_COLORS: Record<string, string> = {
  DRAFT: "bg-gray-100 text-gray-600",
  PENDING_REVIEW: "bg-yellow-100 text-yellow-700",
  REJECTED: "bg-red-100 text-red-700",
  VERIFIED: "bg-blue-100 text-blue-700",
  PUBLISHED: "bg-success-100 text-success-700",
  PAUSED: "bg-warning-100 text-warning-700",
  CLOSED: "bg-gray-100 text-gray-600"
};

type StatusFilter = "ALL" | ProgramStatus;

const FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "ALL", label: "Tất cả" },
  { value: ProgramStatus.DRAFT, label: "Nháp" },
  { value: ProgramStatus.PENDING_REVIEW, label: "Chờ duyệt" },
  { value: ProgramStatus.REJECTED, label: "Bị từ chối" },
  { value: ProgramStatus.VERIFIED, label: "Đã duyệt" },
  { value: ProgramStatus.PUBLISHED, label: "Đang mở" },
  { value: ProgramStatus.PAUSED, label: "Tạm dừng" },
  { value: ProgramStatus.CLOSED, label: "Đã đóng" }
];

function RouteComponent() {
  const { formatDate } = useI18n();
  const navigate = useNavigate();
  const { org } = useRouteContext({
    from: "/_authenticated/org/$orgId/program/"
  });

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");

  const { data, isLoading } = useProgramsQuery({
    orgId: org.id,
    page: 0,
    limit: 20,
    status: statusFilter === "ALL" ? undefined : statusFilter
  });

  const { mutateAsync: deleteProgram } = useDeleteProgram();
  const { mutateAsync: submitForReview } = useSubmitForReview();
  const { mutateAsync: publishProgram } = usePublishProgram();
  const { mutateAsync: pauseProgram } = usePauseProgram();
  const { mutateAsync: resumeProgram } = useResumeProgram();
  const { mutateAsync: closeProgram } = useCloseProgram();

  const programs = data?.data ?? [];

  const handleCreateProgram = () => {
    navigate({ to: "/org/$orgId/program/create", params: { orgId: org.id } });
  };

  const handleSubmitForReview = async (program: ProgramWithDetail) => {
    try {
      await submitForReview({ id: program.id, data: {} });
      toast.success("Đã gửi chương trình để duyệt");
    } catch {
      toast.error("Có lỗi khi gửi duyệt");
    }
  };

  const handlePublish = async (program: ProgramWithDetail) => {
    try {
      await publishProgram(program.id);
      toast.success("Chương trình đã được đăng");
    } catch {
      toast.error("Có lỗi khi đăng chương trình");
    }
  };

  const handlePause = async (program: ProgramWithDetail) => {
    try {
      await pauseProgram(program.id);
      toast.success("Đã tạm dừng chương trình");
    } catch {
      toast.error("Có lỗi khi tạm dừng");
    }
  };

  const handleResume = async (program: ProgramWithDetail) => {
    try {
      await resumeProgram(program.id);
      toast.success("Đã tiếp tục chương trình");
    } catch {
      toast.error("Có lỗi khi tiếp tục");
    }
  };

  const handleClose = async (program: ProgramWithDetail) => {
    if (!window.confirm(`Xác nhận kết thúc chương trình "${program.title ?? ""}"?`)) return;
    try {
      await closeProgram(program.id);
      toast.success("Đã kết thúc chương trình");
    } catch {
      toast.error("Có lỗi khi kết thúc chương trình");
    }
  };

  const handleDelete = async (program: ProgramWithDetail) => {
    if (!window.confirm(`Xác nhận xóa chương trình "${program.title ?? ""}"?`)) return;
    try {
      await deleteProgram(program.id);
      toast.success("Đã xóa chương trình");
    } catch {
      toast.error("Có lỗi khi xóa chương trình");
    }
  };

  const handleApplicants = (programId: string) => {
    navigate({
      to: "/org/$orgId/program/$programId/applicants",
      params: { orgId: org.id, programId }
    });
  };

  const handleQuestions = (programId: string) => {
    navigate({
      to: "/org/$orgId/program/$programId/questions",
      params: { orgId: org.id, programId }
    });
  };

  const columns: TableColumn<ProgramWithDetail>[] = [
    {
      key: "title",
      title: "Tên chương trình",
      dataIndex: "title",
      render: (value) => <span className="font-medium text-text-primary">{value ?? "—"}</span>
    },
    {
      key: "type",
      title: "Loại",
      render: (_, record) => (
        <span className="text-text-secondary">
          {record.type ? (PROGRAM_TYPE_LABELS[record.type] ?? record.type) : "—"}
        </span>
      )
    },
    {
      key: "status",
      title: "Trạng thái",
      render: (_, record) => (
        <span
          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
            PROGRAM_STATUS_COLORS[record.status] ?? "bg-gray-100 text-gray-600"
          }`}
        >
          {PROGRAM_STATUS_LABELS[record.status] ?? record.status}
        </span>
      )
    },
    {
      key: "quota",
      title: "Chỉ tiêu",
      render: (_, record) => record.quota ?? "—"
    },
    {
      key: "deadline",
      title: "Hạn nộp",
      render: (_, record) =>
        record.deadline
          ? formatDate(record.deadline, {
              month: "short",
              day: "numeric",
              year: "numeric"
            })
          : "—"
    },
    {
      key: "actions",
      title: "Thao tác",
      align: "right",
      render: (_, record) => (
        <div onClick={(e) => e.stopPropagation()}>
          <DropdownMenu closeAfterClick>
            <DropdownMenuTrigger asChild>
              <button className="p-1 rounded hover:bg-background-highlight transition-colors">
                <MoreHorizontal className="w-4 h-4 text-text-secondary" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="right">
              <DropdownMenuItem onClick={() => handleApplicants(record.id)}>Ứng viên</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleQuestions(record.id)}>Câu hỏi bổ sung</DropdownMenuItem>
              <DropdownMenuDivider />
              {record.status === ProgramStatus.DRAFT && (
                <DropdownMenuItem onClick={() => handleSubmitForReview(record)}>Gửi duyệt</DropdownMenuItem>
              )}
              {record.status === ProgramStatus.REJECTED && (
                <DropdownMenuItem onClick={() => handleSubmitForReview(record)}>
                  Gửi lại để duyệt
                </DropdownMenuItem>
              )}
              {record.status === ProgramStatus.VERIFIED && (
                <DropdownMenuItem onClick={() => handlePublish(record)}>Đăng chương trình</DropdownMenuItem>
              )}
              {record.status === ProgramStatus.PUBLISHED && (
                <>
                  <DropdownMenuItem onClick={() => handlePause(record)}>Tạm dừng</DropdownMenuItem>
                  <DropdownMenuItem variant="danger" onClick={() => handleClose(record)}>
                    Kết thúc
                  </DropdownMenuItem>
                </>
              )}
              {record.status === ProgramStatus.PAUSED && (
                <>
                  <DropdownMenuItem onClick={() => handleResume(record)}>Tiếp tục</DropdownMenuItem>
                  <DropdownMenuItem variant="danger" onClick={() => handleClose(record)}>
                    Kết thúc
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuDivider />
              <DropdownMenuItem variant="danger" onClick={() => handleDelete(record)}>
                Xóa
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-responsive-xl font-normal text-text-primary">Chương trình</p>
        <Button variant="default" onClick={handleCreateProgram}>
          Tạo chương trình
        </Button>
      </div>

      {/* Status filter */}
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={
              statusFilter === f.value
                ? "px-3 py-1.5 rounded text-responsive-base font-medium bg-primary text-white"
                : "px-3 py-1.5 rounded text-responsive-base font-medium text-primary hover:bg-primary-100 outline outline-primary"
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <Table
        data={programs}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        emptyText='Chưa có chương trình nào. Nhấn "Tạo chương trình" để bắt đầu.'
        hoverable
        onRow={(record) => ({
          onClick: () =>
            navigate({
              to: "/org/$orgId/program/$programId",
              params: { orgId: org.id, programId: record.id }
            }),
          style: { cursor: "pointer" }
        })}
      />
    </div>
  );
}
